// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Group, Modal, Select, SimpleGrid, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { normalizeErrorString } from '@medplum/core';
import type { CarePlan, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  type BillingPathway,
  getBillingPathwayHistory,
  getPatientStatusCategory,
  getPatientSystemStatus,
  getStatusCategoryForTriageStatus,
  isSelfPayPatient,
  PATIENT_INSURANCE_ELIGIBILITY_STATUS,
  PATIENT_STATUS_CATEGORY,
  PATIENT_STATUS_CATEGORY_TITLES,
  PATIENT_SYSTEM_STATUS,
  type PatientStatusCategory,
  POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM,
  POLARIS_STATUS_CATEGORY_TAG_SYSTEM,
  POLARIS_SYSTEM_STATUS_TAG_SYSTEM,
  POLARIS_TRIAGE_STATUS_TAG_SYSTEM,
  replaceTag,
  replaceTriageStatusTags,
  resolveBillingPathway,
  STATUS_CATEGORY_ENTRY_TRIAGE_STATUS,
  withBillingPathwayCompatibilityTag,
  withBillingPathwayDesignationReplacingEffectiveDate,
} from '@polarissleep/shared';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { updatePatientSafely } from '../../../../lib/patient-write';
import { useRole } from '../../../hooks/useRole';
import { setPatientStatusTag } from '../../../utils/patient-status-tags';
import { hasPermission } from '../../../utils/permissions';
import {
  findPatientRpmCarePlan,
  getRpmEnrollmentStatus,
  RPM_ENROLLMENT_UPDATED_EVENT,
  setPatientRpmEnrollment,
} from '../../../utils/rpmEnrollment';
import classes from './PatientStatusBar.module.css';

const SYSTEM_STATUS_OPTIONS = [
  { value: PATIENT_SYSTEM_STATUS.active, label: 'Active' },
  { value: PATIENT_SYSTEM_STATUS.inactive, label: 'Inactive' },
  { value: PATIENT_SYSTEM_STATUS.prospect, label: 'Prospect' },
  { value: PATIENT_SYSTEM_STATUS.archived, label: 'Archived' },
];

const ELIGIBILITY_OPTIONS = [
  { value: PATIENT_INSURANCE_ELIGIBILITY_STATUS.eligible, label: 'Eligible' },
  { value: 'self-pay', label: 'Self-Pay' },
  { value: PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible, label: 'Ineligible' },
];

const RPM_STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
] as const;

const STATUS_CATEGORY_OPTIONS = [
  PATIENT_STATUS_CATEGORY.intake,
  PATIENT_STATUS_CATEGORY.testing,
  PATIENT_STATUS_CATEGORY.treatment,
  PATIENT_STATUS_CATEGORY.referOut,
  PATIENT_STATUS_CATEGORY.ineligible,
].map((value) => ({ value, label: PATIENT_STATUS_CATEGORY_TITLES[value] }));

type EligibilityOptionValue = (typeof ELIGIBILITY_OPTIONS)[number]['value'];

const BILLING_PATHWAY_OPTIONS = [
  { value: 'INSURANCE', label: 'Insurance' },
  { value: 'SELF-PAY', label: 'Self-Pay' },
] as const;

function getTodayDate(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

function readTag(patient: Patient, system: string): string | null {
  return patient.meta?.tag?.find((t) => t.system === system)?.code ?? null;
}

function removeTag(patient: Patient, system: string, code?: string): Patient {
  const tags = patient.meta?.tag ?? [];
  return {
    ...patient,
    meta: {
      ...patient.meta,
      tag: tags.filter((tag) => !(tag.system === system && (code === undefined || tag.code === code))),
    },
  };
}

export interface PatientStatusBarProps {
  patient: Patient;
  onUpdated?: (updated: Patient) => void;
}

export function PatientStatusBar({ patient, onUpdated }: PatientStatusBarProps): JSX.Element {
  const medplum = useMedplum();
  const { role, isAdmin, loading: roleLoading } = useRole();
  const canEdit = hasPermission({ role, permission: 'change-patient-status', isAdmin });

  const [current, setCurrent] = useState<Patient>(patient);
  const [rpmCarePlan, setRpmCarePlan] = useState<CarePlan | undefined>();
  const [saving, setSaving] = useState<'system' | 'eligibility' | 'category' | 'rpm' | 'pathway' | null>(null);
  const [pathwayModalOpen, setPathwayModalOpen] = useState(false);
  const [pendingPathway, setPendingPathway] = useState<BillingPathway>('INSURANCE');
  const [pathwayReason, setPathwayReason] = useState('');
  const [pathwayEffectiveDate, setPathwayEffectiveDate] = useState(getTodayDate);

  const systemStatus = useMemo(() => getPatientSystemStatus(current), [current]);
  const eligibilityTag = readTag(current, POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM);
  const currentPathway = useMemo<BillingPathway | undefined>(() => {
    try {
      return resolveBillingPathway(current, getTodayDate());
    } catch {
      return undefined;
    }
  }, [current]);
  const hasPathwayHistory = getBillingPathwayHistory(current).length > 0;
  const eligibility = useMemo<EligibilityOptionValue | null>(() => {
    if (currentPathway === 'SELF-PAY' || (!hasPathwayHistory && isSelfPayPatient(current))) {
      return 'self-pay';
    }
    if (eligibilityTag === PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible) {
      return PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible;
    }
    if (eligibilityTag === PATIENT_INSURANCE_ELIGIBILITY_STATUS.eligible) {
      return PATIENT_INSURANCE_ELIGIBILITY_STATUS.eligible;
    }
    return null;
  }, [current, currentPathway, eligibilityTag, hasPathwayHistory]);
  const statusCategory = useMemo(() => getPatientStatusCategory(current) ?? null, [current]);
  const rpmStatus = getRpmEnrollmentStatus(current, rpmCarePlan);

  useEffect(() => {
    let active = true;
    if (!current.id) {
      return;
    }
    const refreshRpmCarePlan = (): void => {
      findPatientRpmCarePlan(medplum, current.id as string)
        .then((carePlan) => {
          if (active) {
            setRpmCarePlan(carePlan);
          }
        })
        .catch(() => {
          if (active) {
            setRpmCarePlan(undefined);
          }
        });
    };
    const handleRpmEnrollmentUpdated = (event: Event): void => {
      const patientId = (event as CustomEvent<{ patientId?: string }>).detail?.patientId;
      if (patientId === current.id) {
        refreshRpmCarePlan();
      }
    };
    refreshRpmCarePlan();
    window.addEventListener(RPM_ENROLLMENT_UPDATED_EVENT, handleRpmEnrollmentUpdated);
    return () => {
      active = false;
      window.removeEventListener(RPM_ENROLLMENT_UPDATED_EVENT, handleRpmEnrollmentUpdated);
    };
  }, [current.id, medplum]);

  async function persist(key: 'system' | 'eligibility' | 'category', build: (base: Patient) => Patient): Promise<void> {
    setSaving(key);
    try {
      const updated = await updatePatientSafely(medplum, build(current));
      setCurrent(updated);
      onUpdated?.(updated);
      showNotification({ color: 'green', message: 'Status updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSaving(null);
    }
  }

  async function handleRpmChange(value: string | null): Promise<void> {
    if (!value || value === rpmStatus) {
      return;
    }
    setSaving('rpm');
    try {
      const result = await setPatientRpmEnrollment(medplum, current, value === 'active');
      setCurrent(result.patient);
      setRpmCarePlan(result.carePlan);
      onUpdated?.(result.patient);
      showNotification({ color: 'green', message: `RPM ${value === 'active' ? 'activated' : 'deactivated'}` });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSaving(null);
    }
  }

  function handlePathwayChange(value: string | null): void {
    if (!value) {
      return;
    }
    setPendingPathway(value as BillingPathway);
    setPathwayReason('');
    setPathwayEffectiveDate(getTodayDate());
    setPathwayModalOpen(true);
  }

  async function savePathway(): Promise<void> {
    const today = getTodayDate();
    if (!pathwayReason.trim()) {
      showNotification({ color: 'red', message: 'A reason is required' });
      return;
    }
    if (!pathwayEffectiveDate || pathwayEffectiveDate < today) {
      showNotification({ color: 'red', message: 'Effective date must be today or a future date' });
      return;
    }

    setSaving('pathway');
    try {
      const profile = medplum.getProfile();
      if (!profile?.id) {
        throw new Error('A practitioner profile is required to change the billing pathway');
      }
      const actor = { reference: `${profile.resourceType}/${profile.id}` };
      const designated = withBillingPathwayDesignationReplacingEffectiveDate(current, {
        pathway: pendingPathway,
        effectiveDate: pathwayEffectiveDate,
        reason: pathwayReason.trim(),
        source: 'provider',
        actor,
        previousPathway: currentPathway,
        recordedAt: new Date().toISOString(),
      });
      const incoming = withBillingPathwayCompatibilityTag(designated, today);
      const updated = await updatePatientSafely(medplum, incoming);
      setCurrent(updated);
      onUpdated?.(updated);
      setPathwayModalOpen(false);
      showNotification({ color: 'green', message: 'Billing pathway updated' });
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSaving(null);
    }
  }

  function handleSystemStatusChange(value: string | null): void {
    if (!value || value === systemStatus) {
      return;
    }
    persist('system', (p) => {
      const tagged = setPatientStatusTag(p, POLARIS_SYSTEM_STATUS_TAG_SYSTEM, value);
      return { ...tagged, active: value === PATIENT_SYSTEM_STATUS.active };
    }).catch(console.error);
  }

  async function handleEligibilityChange(value: string | null): Promise<void> {
    if (!value || value === eligibility) {
      return;
    }
    setSaving('eligibility');
    try {
      const profile = medplum.getProfile();
      if (!profile?.id) {
        throw new Error('A practitioner profile is required to change the billing pathway');
      }
      const pathway: BillingPathway = value === 'self-pay' ? 'SELF-PAY' : 'INSURANCE';
      const designated = withBillingPathwayDesignationReplacingEffectiveDate(current, {
        pathway,
        effectiveDate: getTodayDate(),
        reason: `Eligibility changed to ${value}`,
        source: 'provider',
        actor: { reference: `${profile.resourceType}/${profile.id}` },
        previousPathway: currentPathway,
        recordedAt: new Date().toISOString(),
      });
      const withPathwayTag = withBillingPathwayCompatibilityTag(designated, getTodayDate());
      const withoutEligibility = removeTag(withPathwayTag, POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM);
      let code: string | undefined;
      if (value !== 'self-pay') {
        code =
          value === PATIENT_INSURANCE_ELIGIBILITY_STATUS.eligible
            ? PATIENT_INSURANCE_ELIGIBILITY_STATUS.eligible
            : PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible;
      }
      const updated = code
        ? setPatientStatusTag(withoutEligibility, POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM, code)
        : withoutEligibility;
      const written = await updatePatientSafely(medplum, updated);
      setCurrent(written);
      onUpdated?.(written);
    } catch (err) {
      showNotification({ color: 'red', message: normalizeErrorString(err) });
    } finally {
      setSaving(null);
    }
  }

  function handleStatusCategoryChange(value: string | null): void {
    if (!value || value === statusCategory) {
      return;
    }
    // Ineligible lives on the insurance-eligibility tag, so it goes through the same
    // write as the Eligibility select and the two can never disagree.
    if (value === PATIENT_STATUS_CATEGORY.ineligible) {
      handleEligibilityChange(PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible).catch(console.error);
      return;
    }
    const category = value as Exclude<PatientStatusCategory, 'ineligible'>;
    persist('category', (p) => {
      const base = removeTag(
        p,
        POLARIS_INSURANCE_ELIGIBILITY_STATUS_TAG_SYSTEM,
        PATIENT_INSURANCE_ELIGIBILITY_STATUS.ineligible
      );
      const tags = base.meta?.tag ?? [];
      const legacyCategory = getStatusCategoryForTriageStatus(
        readTag(base, POLARIS_TRIAGE_STATUS_TAG_SYSTEM) ?? undefined
      );
      // Keep the legacy triage code (and the Testing milestone it carries) when it is
      // already in the chosen category; otherwise move it to the category's entry code
      // so the queues that still filter on it pick the patient up.
      const nextTags =
        legacyCategory === category
          ? replaceTag(tags, { system: POLARIS_STATUS_CATEGORY_TAG_SYSTEM, code: category })
          : replaceTriageStatusTags(tags, STATUS_CATEGORY_ENTRY_TRIAGE_STATUS[category]);
      return { ...base, meta: { ...base.meta, tag: nextTags } };
    }).catch(console.error);
  }

  const disabled = !canEdit || roleLoading;

  return (
    <Group gap="md" align="flex-end" wrap="wrap">
      {/* type="container" makes the breakpoint responsive to the bar's own width
      rather than the viewport, so the three selects stack when the chart's
      usable area is narrow (e.g. with the sidebar open) regardless of overall
      window size. */}
      <SimpleGrid
        type="container"
        cols={{ base: 1, '32rem': 5 }}
        spacing="md"
        className={classes.bar}
        // SimpleGrid with type="container" renders an outer container-query
        // host div around the inner grid. style/className target the inner
        // grid, so flex-grow has to be applied to the outer wrapper via
        // `styles.container` — otherwise the wrapper shrinks to 0 inside Group.
        styles={{ container: { flex: 1, minWidth: 0 } }}
      >
        <div className={classes.hiddenPathway}>
          <Select
            label="Billing Pathway"
            data={[...BILLING_PATHWAY_OPTIONS]}
            value={currentPathway ?? null}
            onChange={handlePathwayChange}
            disabled={disabled || saving === 'pathway'}
            allowDeselect={false}
            placeholder="Not set"
          />
        </div>
        <Select
          label="System Status"
          data={SYSTEM_STATUS_OPTIONS}
          value={systemStatus}
          onChange={handleSystemStatusChange}
          disabled={disabled || saving === 'system'}
          allowDeselect={false}
        />
        <Select
          label="Eligibility"
          data={ELIGIBILITY_OPTIONS}
          value={eligibility}
          onChange={handleEligibilityChange}
          disabled={disabled || saving === 'eligibility'}
          allowDeselect={false}
          placeholder="Not set"
        />
        <Select
          label="Patient Status"
          data={STATUS_CATEGORY_OPTIONS}
          value={statusCategory}
          onChange={handleStatusCategoryChange}
          disabled={disabled || saving === 'category' || saving === 'eligibility'}
          allowDeselect={false}
          placeholder="Not set"
        />
        <Select
          label="RPM"
          aria-label="RPM Status"
          data={[...RPM_STATUS_OPTIONS]}
          value={rpmStatus}
          onChange={(value) => handleRpmChange(value).catch(console.error)}
          disabled={disabled || saving === 'rpm'}
          allowDeselect={false}
        />
      </SimpleGrid>
      <Modal opened={pathwayModalOpen} onClose={() => setPathwayModalOpen(false)} title="Update billing pathway">
        <Stack>
          <Text size="sm">
            This adds a dated history entry. An existing entry with the same effective date is replaced; entries for
            other dates remain unchanged.
          </Text>
          <Select
            label="Pathway"
            data={[...BILLING_PATHWAY_OPTIONS]}
            value={pendingPathway}
            onChange={(value) => value && setPendingPathway(value as BillingPathway)}
            allowDeselect={false}
          />
          <TextInput
            label="Effective date"
            type="date"
            min={getTodayDate()}
            value={pathwayEffectiveDate}
            onChange={(event) => setPathwayEffectiveDate(event.currentTarget.value)}
            required
          />
          <Textarea
            label="Reason"
            value={pathwayReason}
            onChange={(event) => setPathwayReason(event.currentTarget.value)}
            required
            minRows={3}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPathwayModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => savePathway()} loading={saving === 'pathway'}>
              Save pathway
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Group>
  );
}
